import React, {useContext, useEffect, useCallback, useReducer, useMemo} from 'react';
import PropTypes from 'prop-types';
import getConfig from 'next/config';

import CommentModal from '~/src/components/comment/commentModal/CommentModal';
import Modal from '~/src/components/modal/Modal';
import ToggleButton from '~/src/components/toggleButton/ToggleButton';
import Link from '~/src/components/link/Link';
import HtmlText from '~/src/components/htmlText/HtmlText';

import {createComment, validateComment} from '~/src/utilities/feed/comment';
import {classNames} from '~/src/utilities/helpers/css';
import {Hashes} from '~/src/utilities/constants/hash';
import {SettingsContext} from '~/src/components/context/SettingsContext';
import {UserProfileContext} from '~/src/components/context/UserProfileContext';
import {waitForCondition, loadExternalDependency} from '~/src/utilities/helpers/dependency';
import {SiteArea} from '~/src/utilities/constants/navigation';
import {BeagleEvents} from '~/src/utilities/constants/tracking';
import {trackEvent} from '~/src/utilities/tracking/beagle';
import initPolyglot from '~/src/utilities/libs/polyglot';

import css from './CommentInput.module.scss';
import i18nStrings from './CommentInput.i18n.json';

const {publicRuntimeConfig: config} = getConfig();

const getTranslation = initPolyglot(i18nStrings);

const ActionTypes = {
    OPEN_INPUT: 0,
    CLOSE_INPUT: 1,
    SUBMIT_COMMENT: 2,
    VALUE_CHANGE: 3,
    SET_ERRORS: 4
};

const commentReducer = (state, action) => {
    switch (action.type) {
        case ActionTypes.OPEN_INPUT:
            return {
                ...state,
                inputOpen: true,
                message: null,
                bodyErrors: [],
                nicknameErrors: [],
                liveValidation: false,
                isSubmitting: false
            };
        case ActionTypes.CLOSE_INPUT:
            return {
                inputOpen: false,
                message: action.message,
                bodyErrors: [],
                nicknameErrors: [],
                liveValidation: false,
                isSubmitting: false,
                body: action.body || '',
                nickname: action.nickname || '',
                tocAccepted: action.tocAccepted || false
            };
        case ActionTypes.SUBMIT_COMMENT:
            return {
                ...state,
                isSubmitting: action.isSubmitting
            };
        case ActionTypes.VALUE_CHANGE:
            return {
                ...state,
                body: action.body,
                nickname: action.nickname,
                tocAccepted: action.tocAccepted,
                isSubmitting: false
            };
        case ActionTypes.SET_ERRORS:
            const errors = action.errors || state.errors || [];
            return {
                ...state,
                message: action.message,
                bodyErrors: errors.filter((error) => error.key === 'body'),
                nicknameErrors: errors.filter((error) => error.key === 'authorNickname'),
                liveValidation: true,
                isSubmitting: false
            };
        default:
            return state;
    }
};

const validate = async (nickname, body, dispatch) => {
    const result = await validateComment(nickname, body);

    if (result) {
        const {errors = []} = result;
        dispatch({type: ActionTypes.SET_ERRORS, errors, message: null});
    } else {
        dispatch({type: ActionTypes.SET_ERRORS, message: getTranslation('commentSendFailed')});
    }
};

const CommentInput = ({id, parentId, isReply = false, replyToNickname}) => {
    const {siteArea} = useContext(SettingsContext);
    const {data: userProfile} = useContext(UserProfileContext);

    // default form values
    const defaultValues = {
        body: isReply ? `@${replyToNickname} ` : '',
        nickname: '',
        tocAccepted: false
    };

    // component state / reducer
    const [state, dispatch] = useReducer(commentReducer, {
        ...defaultValues,
        inputOpen: false,
        message: null,
        bodyErrors: [],
        nicknameErrors: [],
        liveValidation: false
    });

    const {body, nickname, tocAccepted, bodyErrors, nicknameErrors, message: popupMessage} = state;

    // calculate on after each change if the form is valid / can be sent
    const isValid = useMemo(
        () =>
            body.length >= 2 && (userProfile?.nickname || nickname.length >= 2) && tocAccepted && !bodyErrors.length && !nicknameErrors.length && !popupMessage,
        [body, nickname, tocAccepted, bodyErrors, nicknameErrors, popupMessage]
    );

    const onToggleInput = useCallback(() => dispatch({type: state.inputOpen ? ActionTypes.CLOSE_INPUT : ActionTypes.OPEN_INPUT, ...defaultValues}), [
        state.inputOpen
    ]);
    const cancelComment = useCallback(() => dispatch({type: ActionTypes.CLOSE_INPUT, ...defaultValues}), []);
    const onBodyChange = useCallback(
        (event) => {
            dispatch({type: ActionTypes.VALUE_CHANGE, nickname, tocAccepted, body: event.target.value});

            if (state.liveValidation) {
                validate(userProfile?.nickname || nickname, event.target.value, dispatch);
            }
        },
        [nickname, tocAccepted, state.liveValidation]
    );
    const onNicknameChange = useCallback(
        (event) => {
            dispatch({type: ActionTypes.VALUE_CHANGE, body, tocAccepted, nickname: event.target.value});

            if (state.liveValidation) {
                validate(event.target.value, body, dispatch);
            }
        },
        [body, tocAccepted, state.liveValidation]
    );
    const onToggleTocs = useCallback(() => dispatch({type: ActionTypes.VALUE_CHANGE, body, nickname, tocAccepted: !tocAccepted}), [
        body,
        nickname,
        tocAccepted
    ]);

    // submit the commment, handle errors if there are any returned
    const sendComment = useCallback(async () => {
        if (isValid) {
            dispatch({type: ActionTypes.SUBMIT_COMMENT, isSubmitting: true});
        }
    }, [body, nickname, isValid]);
    const hideMessage = useCallback(() => dispatch({type: ActionTypes.SET_ERRORS}), []);

    if (process.browser) {
        useEffect(() => {
            loadExternalDependency({src: config.scripts.reCaptcha});

            if (!parentId && location.hash === `#${Hashes.COMMENT_SECTION}`) {
                dispatch({type: ActionTypes.OPEN_INPUT});
            }
        }, []);

        useEffect(() => {
            if (state.isSubmitting) {
                waitForCondition(() => window.grecaptcha && window.grecaptcha.ready).then(() => {
                    window.grecaptcha.ready(() => {
                        window.grecaptcha.execute(config.reCaptchaSiteKey, {action: 'submit'}).then(async (token) => {
                            const beagleEvent = siteArea === SiteArea.VIDEO ? BeagleEvents.VIDEO_COMMENT : BeagleEvents.STORY_COMMENT;
                            trackEvent(beagleEvent, {resourceId: id, comment: body, commenter: nickname});

                            const response = await createComment(id, parentId, userProfile?.nickname || nickname, body, token);
                            if (response) {
                                const {commentId, message, errors = []} = response;

                                if (commentId) {
                                    dispatch({type: ActionTypes.CLOSE_INPUT, message: message || getTranslation('commentSendSuccessful'), ...defaultValues});
                                } else {
                                    dispatch({type: ActionTypes.SET_ERRORS, errors, message});
                                }
                            } else {
                                dispatch({type: ActionTypes.SET_ERRORS, message: getTranslation('commentSendFailed')});
                            }
                        });
                    });
                });
            }
        }, [state.isSubmitting]);
    }

    const charactersLeft = Math.max(config.comment.maximumCharacters.comment - body.length, 0);
    const charCountMax = charactersLeft === 0 ? css.charCountMax : null;
    const inputClassNames = classNames(css, 'commentInput', siteArea, {
        isValid,
        bodyErrors: bodyErrors.length > 0,
        nicknameErrors: nicknameErrors.length > 0
    });

    return (
        <div className={inputClassNames}>
            <div>
                <button className={css.toggleButtonCommentInput} onClick={onToggleInput} data-testid="CommentInputToggle">
                    {getTranslation(isReply ? 'replyToComment' : 'createComment', {nickname: replyToNickname})}

                    {state.inputOpen && (
                        <span className={css.charCounter}>
                            (<span className={charCountMax}>{charactersLeft}</span>)
                        </span>
                    )}
                </button>

                {state.inputOpen && (
                    <React.Fragment>
                        <label className={css.body}>
                            <textarea
                                className={css.bodyField}
                                type="text"
                                value={body}
                                onChange={onBodyChange}
                                data-testid="BodyField"
                                maxLength={config.comment.maximumCharacters.comment}
                            ></textarea>

                            {bodyErrors.length > 0 &&
                                bodyErrors.map((error, index) => (
                                    <div className={css.validationError} key={`body_errors_${index}`}>
                                        {error.message}
                                    </div>
                                ))}
                        </label>

                        <label className={css.nickname}>
                            <span>{getTranslation('nickname')}</span>
                            <input
                                className={css.nicknameField}
                                type="text"
                                value={userProfile?.nickname || nickname}
                                onChange={onNicknameChange}
                                data-testid="NicknameField"
                                maxLength={config.comment.maximumCharacters.nickname}
                                disabled={userProfile?.nickname}
                            />

                            {nicknameErrors.length > 0 &&
                                nicknameErrors.map((error, index) => (
                                    <div className={css.validationError} key={`nickname_errors_${index}`}>
                                        {error.message}
                                    </div>
                                ))}
                        </label>

                        <ToggleButton className={css.toc} checked={tocAccepted} variant="toggleTextRight" callback={onToggleTocs} siteArea={siteArea}>
                            <span className={css.commentRuleText}>
                                {getTranslation('commentRules.partOne')}
                                <Link className={css.tocLink} href={config.comment.commentRules}>
                                    {getTranslation('commentRules.partTwo')}
                                </Link>
                                {getTranslation('commentRules.partThree')}
                            </span>
                        </ToggleButton>

                        <div className={css.controls}>
                            <button className={css.cancelButton} onClick={cancelComment} data-testid="CommentCancelButton">
                                {getTranslation('cancel')}
                            </button>
                            <button className={css.sendButton} onClick={sendComment} data-testid="CommentSendButton" disabled={!isValid}>
                                {getTranslation('send')}
                            </button>
                        </div>

                        <HtmlText className={css.recaptcha} htmlText={getTranslation('recaptchaTnc')} as="div" />
                    </React.Fragment>
                )}
            </div>

            {popupMessage && (
                <Modal>
                    <CommentModal onClose={hideMessage} message={popupMessage} />
                </Modal>
            )}
        </div>
    );
};

if (process.env.NODE_ENV === 'development') {
    CommentInput.propTypes = {
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        parentId: PropTypes.number,
        isReply: PropTypes.bool,
        replyToNickname: PropTypes.string
    };
}

export default CommentInput;
